import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import {
  getClaimTemplates,
  createClaimTemplate,
  updateClaimTemplate,
  deleteClaimTemplate,
} from "../../apis/insuranceProviders";
import { getServiceCategories } from "../../apis/serviceCategory";

interface Props {
  open: boolean;
  onClose: () => void;
  providerId: number;
  providerName: string;
}

const emptyTemplate = {
  providerLabel: "",
  serviceCategoryId: "",
  displayOrder: 0,
  isVisible: true,
};

const ClaimTemplatesModal: React.FC<Props> = ({
  open,
  onClose,
  providerId,
  providerName,
}) => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [form, setForm] = useState({ ...emptyTemplate });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    fetchTemplates();
    fetchCategories();
    resetForm();
  }, [open, providerId]);

  const fetchTemplates = async () => {
    try {
      const res: any = await getClaimTemplates(providerId);
      setTemplates(res.data || res);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCategories = async () => {
    try {
      const res: any = await getServiceCategories({ per_page: 200 });
      setCategories(res.data || res);
    } catch (e) {
      console.error(e);
    }
  };

  const resetForm = () => {
    setForm({ ...emptyTemplate });
    setEditingId(null);
    setErrors({});
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.providerLabel.trim()) e.providerLabel = "* Label is required";
    if (!form.serviceCategoryId) e.serviceCategoryId = "* Category is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      if (editingId) {
        await updateClaimTemplate(providerId, editingId, form);
      } else {
        await createClaimTemplate(providerId, form);
      }
      await fetchTemplates();
      resetForm();
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (t: any) => {
    setEditingId(t.id);
    setForm({
      providerLabel: t.providerLabel || t.provider_label,
      serviceCategoryId:
        t.serviceCategoryId ?? t.service_category_id ?? "",
      displayOrder: t.displayOrder ?? t.display_order ?? 0,
      isVisible: t.isVisible ?? t.is_visible ?? true,
    });
    setErrors({});
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm("Delete this claim template mapping?")) return;
    try {
      await deleteClaimTemplate(providerId, templateId);
      fetchTemplates();
    } catch (e) {
      console.error(e);
    }
  };

  if (!open) return null;

  return (
    <Modal
      title={`Claim Templates — ${providerName}`}
      onClose={onClose}
      modalContent={
        <div className="space-y-6">
          {/* Add / edit form */}
          <div className="border rounded-md p-4 bg-gray-50 space-y-3">
            <h3 className="text-sm font-medium text-gray-700">
              {editingId ? "Edit Mapping" : "Add Mapping"}
            </h3>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-gray-600">
                  Provider Label
                  <span className="ml-1 text-gray-400">(what the insurer calls it)</span>
                </label>
                <input
                  type="text"
                  value={form.providerLabel}
                  onChange={(e) => setForm({ ...form, providerLabel: e.target.value })}
                  placeholder="e.g. Professional Charges"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
                {errors.providerLabel && (
                  <span className="text-xs text-red-500">{errors.providerLabel}</span>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600">
                  Internal Service Category
                  <span className="ml-1 text-gray-400">(our canonical code)</span>
                </label>
                <select
                  value={form.serviceCategoryId}
                  onChange={(e) =>
                    setForm({ ...form, serviceCategoryId: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                >
                  <option value="">Select category...</option>
                  {categories.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName || c.display_name} ({c.code})
                    </option>
                  ))}
                </select>
                {errors.serviceCategoryId && (
                  <span className="text-xs text-red-500">{errors.serviceCategoryId}</span>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600">Display Order</label>
                <input
                  type="number"
                  min={0}
                  value={form.displayOrder}
                  onChange={(e) =>
                    setForm({ ...form, displayOrder: Number(e.target.value) })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
              </div>

              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.isVisible}
                    onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                  />
                  Visible on claim form
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="px-3 py-1.5 text-sm bg-primary text-white rounded"
              >
                {editingId ? "Update" : "Add"}
              </button>
              {editingId && (
                <button
                  onClick={resetForm}
                  className="px-3 py-1.5 text-sm border rounded"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Template list */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Mappings ({templates.length})
            </h3>
            {templates.length === 0 ? (
              <p className="text-sm text-gray-500">No mappings yet.</p>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-2 px-3 text-left font-semibold text-gray-900">
                        Provider Label
                      </th>
                      <th className="py-2 px-3 text-left font-semibold text-gray-900">
                        Internal Category
                      </th>
                      <th className="py-2 px-3 text-left font-semibold text-gray-900">
                        Visible
                      </th>
                      <th className="py-2 px-3 text-left font-semibold text-gray-900">
                        Order
                      </th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {templates.map((t: any) => (
                      <tr key={t.id}>
                        <td className="py-2 px-3 font-medium text-gray-900">
                          {t.providerLabel || t.provider_label}
                        </td>
                        <td className="py-2 px-3 text-gray-700">
                          {t.serviceCategory?.displayName ||
                            t.serviceCategory?.display_name ||
                            t.serviceCategoryCode ||
                            t.service_category_code}
                        </td>
                        <td className="py-2 px-3 text-gray-700">
                          {(t.isVisible ?? t.is_visible) ? "Yes" : "No"}
                        </td>
                        <td className="py-2 px-3 text-gray-700">
                          {t.displayOrder ?? t.display_order}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(t)}
                              className="px-2 py-1 text-xs bg-primary text-white rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(t.id)}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};

export default ClaimTemplatesModal;
